import { gridClasses } from "@mui/x-data-grid";
import { capitalize } from "../../../../../utils/stringUtil";
import { formatLastUpdated } from "../../../../../utils/tableUtil";
import { ellipse } from "../../../../../utils/stringUtil";
import { convertUtcToTimezone } from "../../../../../utils/dateUtil";

interface FormControl {
  value: unknown;
  required?: boolean;
}

const ColumnSizeCondition = {
  inputMaxLength: 100,
  maxColumnSize: 300,
};

export interface ColumnSpec {
  id?: string;
  field?: string;
  headerName?: string;
  label?: string;
  lockedForEdit?: boolean;
  type?: string;
  uiPosition?: number;
  required?: boolean;
  inputMaxLength?: number;
  partitionKey?: boolean;
  sortKey?: string;
  sortable: boolean;
  fieldVisible: boolean;
  selectValues?: unknown;
  flex?: number;
  maxWidth?: number;
  minWidth?: number;
  renderCell?: (params: Record<string, unknown>, timezone?: string) => unknown;
  filterable?: boolean;
  width?: number;
  cellClassName?: string;
  valueGetter?: (params: Record<string, unknown>) => unknown;
  renderHeader?: () => unknown;
  getApplyQuickFilterFn?: (
    quickFilterValue: unknown,
  ) => (value: unknown) => boolean;
  display?: string;
  align?: string;
  [key: string]: unknown;
}

export interface ContentSpec {
  columns: ColumnSpec[];
  columnVisibilityModel: Record<string, boolean>;
  getRowHeight: () => string;
  getEstimatedRowHeight: () => number;
  sx: Record<string, unknown>;
}

interface TableItem {
  friendlyName?: string;
  tableName?: string;
  uiPosition?: number;
}

interface Attribute {
  name?: string;
  friendlyName?: string;
  lockedForEdit?: boolean;
  type?: string;
  uiPosition?: number;
  required?: boolean;
  maxLength?: number;
  partitionKey?: boolean;
  sortKey?: string;
  hideOnUi?: boolean;
  selectValues?: unknown;
  [key: string]: unknown;
}

export const commonRenderCell = (
  params: Record<string, unknown>,
  timezone?: string,
): unknown => {
  const { field, row, value, colDef } = params;
  if (field === "lastUpdated") {
    return (row as Record<string, unknown>)?.lastUpdated
      ? formatLastUpdated(
          row as {
            lastUpdatedBy?: string | null;
            lastUpdated?: string | null;
            [key: string]: unknown;
          },
          timezone ?? "",
        )
      : "";
  }

  if (field === "dateTime" || field === "datetime") {
    return value || "No date";
  }

  if (
    field !== "action" &&
    (colDef as Record<string, unknown>)?.type === "boolean" &&
    typeof value === "boolean"
  ) {
    return `${value}`;
  }

  return ellipse(value as string, 150);
};

/**
 * Render cell function to present boolean values for cells where colDef.type === boolean
 * @param  {any} value               a value
 * @return {string}       presentation value
 */
export const booleanRenderCell = ({ value }: { value: unknown }): string => {
  return value ? capitalize(`${value}`, true) : "";
};

/**
 * Normalize a value
 * @param  {any} value               any value
 * @return {any}       normalized value
 */
const normalizeValue = (value: unknown): unknown => {
  const booleanValues = new Set(["true", "false"]);

  if (typeof value === "boolean") return `${value}`;

  const lowerCaseValue =
    typeof value === "string" ? value.trim().toLowerCase() : undefined;
  if (lowerCaseValue && booleanValues.has(lowerCaseValue)) {
    return `${lowerCaseValue === "true"}`;
  }

  return value;
};

/**
 * Data normalizer to prepare field.values with transform values. This practice allows filtering
 * to be effective in calculated values
 * @param  {Array} dataset                dataset to map
 * @param  {Array} colSpecs               list of columns
 * @param  {string} timezone               time zone code
 * @return {Array}          normalized array
 */
export const dataNormalizer = (
  dataset: Record<string, unknown>[],
  colSpecs: ColumnSpec[],
  timezone: string,
): Record<string, unknown>[] => {
  const dataTypeMap = new Map<string, string>(
    colSpecs
      .filter(
        (col): col is ColumnSpec & { type: string; id: string } =>
          col.type === "boolean" && col.id != null,
      )
      .map((col): [string, string] => [col.id as string, col.type as string]),
  );
  return dataset.map((record) => {
    const normalRecord: Record<string, unknown> = { ...record };

    // Handle both camelCase (dateTime) and lowercase (datetime) ISO date fields
    for (const dtField of ["dateTime", "datetime"] as const) {
      if (normalRecord[dtField]) {
        normalRecord[dtField] = convertUtcToTimezone(
          normalRecord[dtField] as string,
          timezone,
        );
      }
    }

    dataTypeMap.forEach((_value, key) => {
      const recordValue = normalRecord[key];
      normalRecord[key] = normalizeValue(recordValue);
    });

    return normalRecord;
  });
};

/**
 * Content normalization function
 * @param  {Object} attributes               content attributes
 * @return {Object}            content spec object
 */
export const normalizeContentSpec = (attributes: Attribute[]): ContentSpec => {
  const columns: ColumnSpec[] = [];
  const columnVisibilityModel: Record<string, boolean> = {};
  for (const item of attributes) {
    const maxLen = item.maxLength ?? 0;
    columns.push({
      id: item.name ?? "",
      field: item.name ?? "",
      headerName: item.friendlyName ?? "",
      label: item.friendlyName ?? "",
      lockedForEdit: item.lockedForEdit,
      type: item.type,
      uiPosition:
        item.uiPosition !== undefined && item.uiPosition !== null
          ? Number(item.uiPosition)
          : undefined,
      required: item.required,
      inputMaxLength: item.maxLength,
      partitionKey: item.partitionKey ? item.partitionKey : false,
      sortKey: item.sortKey,
      sortable: true,
      fieldVisible: !item.hideOnUi,
      selectValues: item.selectValues,
      flex: maxLen > ColumnSizeCondition.inputMaxLength ? 1 : undefined,
      maxWidth:
        maxLen > ColumnSizeCondition.inputMaxLength
          ? ColumnSizeCondition.maxColumnSize
          : undefined,
      renderCell:
        item.type === "boolean"
          ? (p: Record<string, unknown>) =>
              booleanRenderCell({ value: p.value })
          : commonRenderCell,
    });

    if (item.hideOnUi && item.name) {
      columnVisibilityModel[item.name] = false;
    }
  }

  columns.sort((a, b) => (a.uiPosition ?? 0) - (b.uiPosition ?? 0));

  const wideContent = columns.some((column) => column.maxWidth);
  const getRowHeight = () => "auto" as const;
  const getEstimatedRowHeight = () => 32;
  const sx = wideContent
    ? {
        [`& .${gridClasses.cell}`]: {
          py: 1,
        },
      }
    : {};

  return {
    columns,
    columnVisibilityModel,
    getRowHeight,
    getEstimatedRowHeight,
    sx,
  };
};

/**
 * Normalization of table list
 * @param  {Array}  [tableList=[]]               list of tables
 * @return {Array}                normalize version of the table list
 */
export const normalizeTableList = (
  tableList: TableItem[] = [],
): {
  name: string | undefined;
  value: string | undefined;
  uiPosition: number | undefined;
}[] => {
  return tableList
    .map((item) => ({
      name: item.friendlyName,
      value: item.tableName,
      uiPosition: item.uiPosition,
    }))
    .sort((a, b) => (a.uiPosition ?? 0) - (b.uiPosition ?? 0));
};

/**
 * Returns a list of columns that present data
 * @param  {Array} columns               list of column specs
 * @return {Array}         list of columns
 */
export const getDataColumns = (columns: ColumnSpec[]): ColumnSpec[] => {
  return columns.filter((column) => column?.id !== "action");
};

/**
 * Return a list of key-value pairs of the given row object
 * @param  {Object} row               a row
 * @return {Array}     a list of properties of the row
 */
export const getRowAttributes = (
  row: Record<string, unknown> | null,
): { name: string; value: unknown }[] => {
  const attributes: { name: string; value: unknown }[] = [];
  if (!row) return attributes;
  for (const key in row) {
    key !== "__rowId" && attributes.push({ name: key, value: row[key] });
  }
  return attributes;
};

/**
 * Computes the default sorting model for a data grid.
 * @param {Array} columns - Array of column definitions.
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @returns {Array} - The default sort model (e.g., [{ field: "columnId", sort: "asc" }]) or an empty array.
 */
export const getDefaultSortModel = (
  columns: ColumnSpec[] | undefined,
  sortDirection: string,
): { field: string; sort: string }[] => {
  if (!columns || !Array.isArray(columns)) {
    return [];
  }

  const defaultSortingField = columns?.find((col) => col.uiPosition === 1)?.id;

  if (defaultSortingField) {
    return [{ field: defaultSortingField, sort: sortDirection }];
  }

  return [];
};

/**
 * Validates form values for required fields
 * @param {Map} values - Map of form values to validate
 * @return {boolean} - Returns true if there are validation errors, false otherwise
 */
export const validateFormValues = (
  values: Map<
    string,
    { value?: unknown; required?: boolean; [key: string]: unknown }
  >,
): boolean => {
  let hasError = false;

  for (const [, control] of values.entries()) {
    const isEmpty =
      control.value === undefined ||
      control.value === null ||
      control.value.toString().trim().length === 0;

    if (control.required && isEmpty) {
      hasError = true;
      break;
    }
  }

  return hasError;
};
